import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import SettingHint from '../SettingHint'

import { getActivationRequirements, getRequirementCatalog } from '../../../../../../shared/activationRequirements'

import styles from '../style.module.scss'
import preview from './style.module.scss'

/**
 * Vorschau der Checkliste, die eine Schüler:in vor dem Einreichen zur Prüfung
 * sieht.
 *
 * Die Liste ist hier bewusst nicht bearbeitbar: Was sich einstellen lässt,
 * steht als Karte darunter — die Vorschau sagt, wie sich das auswirkt.
 *
 * Gezeigt wird sie ohne Häkchen und in ruhigen Farben. Grün und Orange stehen
 * bei den Schüler:innen für „erfüllt" und „fehlt noch"; hier gibt es keine
 * Company, auf die sich das beziehen könnte.
 *
 * Die Verweise („Zu den Mitarbeiter:innen") stehen mit: Sie sind Teil dessen,
 * was die Schüler:innen sehen, und wer die Vorschau prüft, prüft auch, ob sie
 * ans richtige Ziel führen.
 */
export default function ActivationRequirements(props) {
	const { t } = useTranslation()

	// `hygienePreview` und `conceptPreview`: Die Hygiene-Bestätigung gilt nur für
	// einzelne Branchen, das Skills4Retail-Konzept nur dort, wo es vor der
	// Aktivierung sperrt — beide bleiben in der Checkliste der Schüler:innen
	// meist weg. In der Vorschau sollen sie trotzdem stehen, sonst gäbe es
	// keinen Ort, an dem die Administration sie je zu Gesicht bekommt.
	const requirements = getActivationRequirements({
		options: props.form,
		hygienePreview: true,
		conceptPreview: true
	})
	const switchedOff = getRequirementCatalog(props.form).filter(row => !row.active)

	return (
		<div id="activationRequirements" className={styles.root}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>{t('Checklist as students see it')}</h3>
				<SettingHint
					example={t(
						'A student sees this list when clicking "Submit for review". Submitting is only possible once every item is met.'
					)}
				>
					{t(
						'These conditions must be met before a Junior Company can be submitted for review and activated. Which of them apply is set in the card below.'
					)}
				</SettingHint>
				<div className={styles.sectionContent}>
					<ol className={preview.list}>
						{requirements.map(requirement => (
							<li key={requirement.id} className={preview.item}>
								<span className={preview.marker} aria-hidden="true" />
								<div className={preview.body}>
									<p className={preview.title}>{requirement.title}</p>
									<p className={preview.text}>{requirement.text}</p>
									{requirement.link && (
										<p className={preview.linkLine}>
											{/* Ein Verweis nach draußen (der Kurs) ist ein
											    gewöhnlicher Link, einer in die Anwendung geht
											    über den Router — sonst lädt die ganze Seite neu. */}
											{requirement.link.startsWith('/') ? (
												<Link className={preview.link} to={requirement.link}>
													{requirement.linkLabel}
												</Link>
											) : (
												<a
													className={preview.link}
													href={requirement.link}
													target="_blank"
													rel="noopener noreferrer"
												>
													{requirement.linkLabel}
												</a>
											)}
										</p>
									)}
								</div>
							</li>
						))}
					</ol>

					{/* Ohne diesen Hinweis bliebe unklar, warum die Liste kürzer ist als
					    die Konfiguration darunter vermuten lässt. */}
					{switchedOff.length > 0 && (
						<p className={preview.note}>
							{t('Switched off and therefore not shown to students')}:{' '}
							{switchedOff.map(row => row.title).join(', ')}
						</p>
					)}
				</div>
			</section>
		</div>
	)
}

ActivationRequirements.propTypes = {
	form: PropTypes.object.isRequired
}
